import { Sparkles, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";

const kb = (n) => `${(n / 1024).toFixed(0)} KB`;

/** A saved version and the live one, side by side, with a plain verdict on
 *  whether anything load-bearing actually differs — so nobody restores a
 *  revision on a hunch. */
export const KbCompareDialog = ({ c, onClose }) => (
  <div className="fixed inset-0 z-[60] flex flex-col bg-slate-900/60 p-4 backdrop-blur-sm"
    data-testid="kb-compare-dialog">
    <div className="mx-auto flex h-full w-full max-w-[1600px] flex-col overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-5 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-heading text-base font-semibold text-slate-800 dark:text-slate-100">{c.name}</h2>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            {c.content_identical ? (
              <span className="flex items-center gap-1 font-semibold text-emerald-700" data-testid="kb-compare-verdict-identical">
                <CheckCircle2 className="h-3.5 w-3.5" /> Same words, images, tables, links and callouts — only the markup differs
              </span>
            ) : c.text_identical ? (
              <span className="flex items-center gap-1 font-semibold text-emerald-700" data-testid="kb-compare-verdict-text-same">
                <CheckCircle2 className="h-3.5 w-3.5" /> Every word is still here —{" "}
                {Object.keys(c.changed).filter((k) => !["bytes", "inline_styles", "words", "headings"].includes(k)).join(", ") || "markup only"} differs
              </span>
            ) : (
              <span className="flex items-center gap-1 font-semibold text-amber-700" data-testid="kb-compare-verdict-changed">
                <AlertTriangle className="h-3.5 w-3.5" /> The wording differs between these two — read both panes
              </span>
            )}
            <span className="text-slate-400 dark:text-slate-500">
              {kb(c.before.bytes)} → {kb(c.after.bytes)} · {c.before.words} words → {c.after.words}
            </span>
          </p>
        </div>
        <button onClick={onClose} data-testid="kb-compare-close"
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-rose-300 hover:text-rose-600">
          <X className="h-3.5 w-3.5" /> Close
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 divide-x divide-slate-200 dark:divide-slate-800 lg:grid-cols-2">
        <Pane label={c.note || "Saved version"} sub={`saved ${(c.at || "").slice(0, 16).replace("T", " ")}${c.by ? ` by ${c.by}` : ""}`}
          html={c.before_html} shape={c.before} testid="kb-compare-before" muted />
        <Pane label="Live now" sub="what readers see" html={c.after_html} shape={c.after}
          testid="kb-compare-after" />
      </div>
    </div>
  </div>
);

const Pane = ({ label, sub, html, shape, testid, muted }) => {
  const embedHosts = useKbEmbedHosts();
  return (
  <div className="flex min-h-0 flex-col" data-testid={testid}>
    <div className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-xs ${
      muted ? "border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 text-slate-500 dark:text-slate-400" : "border-emerald-100 bg-emerald-50/60 text-emerald-800"}`}>
      <span className="flex min-w-0 items-center gap-1.5 font-semibold">
        {!muted && <Sparkles className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{label}</span>
        <span className="truncate font-normal opacity-70">· {sub}</span>
      </span>
      <span className="shrink-0 opacity-70">
        {shape.words} words · {shape.images} images · {shape.tables} tables
      </span>
    </div>
    <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
      <div className="kb-prose" dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(html, embedHosts) }} />
    </div>
  </div>
  );
};
